// Pure computation behind the topbar health chip — extracted from chrome.tsx so
// the health semantics are unit-testable without rendering.
//
// The chip mirrors the ALERTS TABLE (user decision, 2026-08-31): its tier is the
// worst severity among ACTIVE incidents — the same population the Triggered tab
// shows by default — plus one belt-and-braces channel: a component reported
// `down` by the server-composed HealthSummary (config decrypt failure, dead
// batch heartbeat) still forces critical, because those states may have no
// alert row. Everything else the chip used to aggregate — degraded components
// and the system-metric BANDS (which in prod carry dozens of per-production
// quality readings with no incident) — is now informational: it renders in the
// popover with its deep-link but never colors the pill.
//
// Acked incidents never color the pill (they are counted for the popover);
// only `active` does. A feed failure is STALENESS, not an incident: when the
// alerts feed errors, or the health feed errors with nothing cached, the tier
// floors at `unknown` ("Status stale") instead of fabricating warn/critical.
// Liveness stays a POSITIVE signal only — `down` comes from the server, never
// inferred from log absence.

import type {
  Alert,
  HealthSummary,
} from '@/api/types';
import type {
  AlertSeverity,
  HealthStatus,
  SystemHealthResponse,
} from '@/api/types.gen';
import type { Band } from './system-metrics';

export function relTime(ms: number, now: number = Date.now()): string {
  const d = now - ms;
  if (d < 90_000) return 'just now';
  if (d < 3_600_000) return `${Math.round(d / 60_000)} min ago`;
  if (d < 86_400_000) return `${Math.round(d / 3_600_000)}h ago`;
  return `${Math.round(d / 86_400_000)}d ago`;
}

/** The pill's tier — Band plus a distinct `error` step so an error-severity
 * incident no longer masquerades as "Critical". Chip-local vocabulary; the
 * shared metric `Band` is untouched. */
export type ChipTier = 'ok' | 'unknown' | 'warn' | 'error' | 'critical';

const TIER_RANK: Record<ChipTier, number> = { ok: 0, unknown: 1, warn: 2, error: 3, critical: 4 };
const BAND_RANK: Record<Band, number> = { ok: 0, unknown: 1, warn: 2, critical: 3 };

function worseTier(a: ChipTier, b: ChipTier): ChipTier {
  return TIER_RANK[b] > TIER_RANK[a] ? b : a;
}

export interface ChipCheck {
  lbl: string;
  detail: string;
  stamp: string;
  /** OK == band 'ok'. Kept for existing callers/tests. */
  ok: boolean;
  band: Band;
  /** App route to jump to the problem (popover row becomes a link). */
  link?: string;
}

/** One active incident surfaced in the popover's alerts section. */
export interface ChipTopAlert {
  id: string;
  title: string;
  severity: AlertSeverity;
  lastSeenMs: number;
}

export interface ChipInputs {
  /** Server-composed component health; undefined while the first poll is in flight. */
  health: HealthSummary | undefined;
  healthError: boolean;
  alerts: Alert[];
  alertsError: boolean;
  /** Role has backend_view — the alerts section is omitted entirely when false (endpoint 403s). */
  alertsEnabled: boolean;
  /** Unified system-metric bands; undefined while loading or when the role can't see them. */
  metrics?: SystemHealthResponse;
  metricsError?: boolean;
  /** Injectable clock for tests; defaults to Date.now(). */
  now?: number;
}

export interface ChipState {
  /** Informational signal rows (components + non-ok metrics), worst first.
   * They render in the popover with their deep-links but — apart from a
   * component that is `down` — never color the pill. */
  checks: ChipCheck[];
  /** Drives the pill color/label. */
  tier: ChipTier;
  /** ACTIVE incidents by severity (the population the Triggered tab shows). */
  activeCounts: { critical: number; error: number; warn: number };
  activeTotal: number;
  /** Acked-but-unresolved incidents — shown in the popover, never colored. */
  ackedCount: number;
  /** Up to 3 active incidents, worst severity then newest, for the popover. */
  topAlerts: ChipTopAlert[];
  /** True when the alerts feed is erroring (popover shows a stale note). */
  alertsStale: boolean;
  /** `tier` collapsed onto the shared Band vocabulary (error ⇒ critical). */
  band: Band;
  /** `band === 'ok'` — retained so existing callers keep working. */
  ok: boolean;
  /** Worst severity among ACTIVE alerts; null when none. */
  worstSeverity: AlertSeverity | null;
}

const SEVERITY_RANK: Record<string, number> = { warn: 1, error: 2, critical: 3 };

/** A component's liveness status → a paintable band for its signal row.
 *
 * This includes the `config` component: an unreadable managed configuration
 * reds the chip (only `down` aggregates into the tier), deliberately — it
 * means provider credentials cannot be decrypted, which is an incident, not a
 * detail. No carve-out. */
function componentBand(status: HealthStatus): Band {
  if (status === 'down') return 'critical';
  if (status === 'degraded') return 'warn';
  return 'ok'; // up | quiet
}

// Human labels for the server's stable component ids; falls back to a
// de-underscored id for any component the server adds later. The ids never
// change (stored rows, log signatures, config keys); only these words do.
// `shared::component_label` is the Rust twin, and
// shared/testdata/component_labels/cases.json holds both to the same answers.
export const COMPONENT_LABEL: Readonly<Record<string, string>> = {
  api_worker: 'SAM Server · API',
  batch_worker: 'SAM Server · Jobs',
  pixel_worker: 'Pixel Collector',
  frontend: 'Frontend',
  ingest: 'EncorePro ingest',
  audience_benchmarks: 'Buyer benchmarks',
  pixel_flow: 'Pixel data',
  data_stores: 'Data Warehouse & Lake',
  config: 'Managed config',
  meta_credential: 'Meta credential',
  ttd_credential: 'TTD credential',
  xero_credential: 'Xero credential',
  postgres: 'Data Warehouse',
  trino: 'Data Lake',
  lake: 'Data Lake',
};

/** Hyphenated spellings older rows and log lines carry for the same ids. */
export const COMPONENT_ALIAS: Readonly<Record<string, string>> = {
  'api-worker': 'api_worker',
  'batch-worker': 'batch_worker',
  'pixel-worker': 'pixel_worker',
};

/** One component id → one human label, everywhere it is shown. */
export function componentLabel(id: string): string {
  const canonical = COMPONENT_ALIAS[id] ?? id;
  return COMPONENT_LABEL[canonical] ?? canonical.replace(/_/g, ' ');
}

type StoreWordText = 'ok' | 'degraded' | 'down' | 'unknown';
const STORE_WORD_RANK: Record<StoreWordText, number> = { ok: 0, unknown: 1, degraded: 2, down: 3 };

/** The `data_stores` detail in the words people read.
 *
 * The server keeps its machine form, `postgres <word>[ (migration N…)] ·
 * trino <word> · lake <word>[ (sealed through D[, note])]` or `lake sealed
 * through D` (`dataStoreWords` in flow-layout.ts and api health's tests pin
 * it). Shown, it reads "Data Warehouse ok · Data Lake ok, sealed through D":
 * Trino and the lake are one Data Lake, at the worse of their two words.
 * Anything unrecognised ("no store report", "heartbeat stale") is shown as
 * the server wrote it. */
export function formatStoreDetail(detail: string | null | undefined): string {
  const raw = detail ?? '';
  let warehouse: string | null = null;
  let lakeWords: StoreWordText[] = [];
  let sealed: string | null = null;
  let note: string | null = null;
  let recognised = false;
  for (const part of raw.split(' \u00b7 ')) {
    let m = /^postgres (ok|degraded|down|unknown)(?: \((.+)\))?$/.exec(part);
    if (m) {
      warehouse = `Data Warehouse ${m[1]}${m[2] ? ` (${m[2]})` : ''}`;
      recognised = true;
      continue;
    }
    m = /^trino (ok|degraded|down|unknown)$/.exec(part);
    if (m) {
      lakeWords = [...lakeWords, m[1] as StoreWordText];
      recognised = true;
      continue;
    }
    m = /^lake sealed through (\S+)$/.exec(part);
    if (m) {
      lakeWords = [...lakeWords, 'ok'];
      sealed = m[1] ?? null;
      recognised = true;
      continue;
    }
    m = /^lake (ok|degraded|down|unknown)(?: \((.+)\))?$/.exec(part);
    if (m) {
      lakeWords = [...lakeWords, m[1] as StoreWordText];
      const inner = m[2] ?? '';
      const sealedMatch = /^sealed through (\S+?)(?:, (.+))?$/.exec(inner);
      if (sealedMatch) {
        sealed = sealedMatch[1] ?? null;
        note = sealedMatch[2] ?? null;
      } else if (inner) {
        note = inner;
      }
      recognised = true;
      continue;
    }
    return raw;
  }
  if (!recognised) return raw;
  const words: string[] = [];
  if (warehouse) words.push(warehouse);
  if (lakeWords.length > 0) {
    const worst = lakeWords.reduce((a, b) => (STORE_WORD_RANK[b] > STORE_WORD_RANK[a] ? b : a));
    words.push(`Data Lake ${worst}${sealed ? `, sealed through ${sealed}` : ''}${note ? ` (${note})` : ''}`);
  }
  return words.join(' \u00b7 ');
}

/** A component's detail as shown: the store report reworded, everything else as sent. */
export function componentDetail(id: string, detail: string): string {
  return id === 'data_stores' ? formatStoreDetail(detail) : detail;
}

export function computeChipState(inp: ChipInputs): ChipState {
  const now = inp.now ?? Date.now();
  let tier: ChipTier = 'ok';

  // ── Component health: one signal row per server-composed component. Only a
  // hard `down` forces the tier (belt-and-braces for states with no alert row);
  // `degraded` renders amber in the popover without coloring the pill. On a
  // transient poll error we keep showing the last-known-good components rather
  // than flashing the chip on a single blip; only when there is no data at all
  // do we surface one unreachable row — as staleness, never as an incident. ──
  const healthChecks: ChipCheck[] =
    inp.healthError && !inp.health
      ? [{ lbl: 'System health', detail: 'health endpoint unreachable', stamp: '—', ok: false, band: 'unknown' }]
      : (inp.health?.components ?? []).map((c) => {
          const band = componentBand(c.status);
          return {
            lbl: componentLabel(c.component),
            detail: componentDetail(c.component, c.detail),
            stamp: c.last_seen ? relTime(+new Date(c.last_seen), now) : '—',
            ok: band === 'ok',
            band,
          };
        });
  if (inp.healthError && !inp.health) tier = worseTier(tier, 'unknown');
  for (const c of inp.health?.components ?? []) {
    if (c.status === 'down') tier = worseTier(tier, 'critical');
  }

  // ── Alerts: the population that DRIVES the chip. Active only; worst severity
  // maps 1:1 onto the tier. Acked is counted for the popover line. ──
  const activeCounts = { critical: 0, error: 0, warn: 0 };
  let ackedCount = 0;
  let worst: AlertSeverity | null = null;
  const active: Alert[] = [];
  if (inp.alertsEnabled) {
    for (const a of inp.alerts) {
      if (a.status === 'acked') ackedCount += 1;
      if (a.status !== 'active') continue;
      active.push(a);
      const sev = (a.severity in activeCounts ? a.severity : 'warn') as keyof typeof activeCounts;
      activeCounts[sev] += 1;
      if (worst == null || (SEVERITY_RANK[a.severity] ?? 0) > (SEVERITY_RANK[worst] ?? 0)) {
        worst = a.severity as AlertSeverity;
      }
    }
    if (worst) tier = worseTier(tier, worst as ChipTier);
    if (inp.alertsError) tier = worseTier(tier, 'unknown');
  }
  const topAlerts: ChipTopAlert[] = active
    .slice()
    .sort(
      (a, b) =>
        (SEVERITY_RANK[b.severity] ?? 0) - (SEVERITY_RANK[a.severity] ?? 0) ||
        (+new Date(b.last_seen) || 0) - (+new Date(a.last_seen) || 0),
    )
    .slice(0, 3)
    .map((a) => ({
      id: a.id,
      title: a.title,
      severity: a.severity as AlertSeverity,
      lastSeenMs: +new Date(a.last_seen) || 0,
    }));

  // ── System-metric signals: one informational row per non-OK metric —
  // warn/critical bands AND stale/`unknown` metrics. Each deep-links to its
  // screen; none of them colors the pill (prod carries dozens of critical
  // per-production quality bands with zero incidents — that is exactly the
  // mismatch this redesign removes). ──
  const metricChecks: ChipCheck[] = (inp.metrics?.metrics ?? [])
    .map((m) => ({ m, band: (m.stale ? 'unknown' : (m.band as Band)) as Band }))
    .filter(({ band }) => band !== 'ok')
    .map(({ m, band }) => ({
      lbl: m.label,
      detail:
        band === 'unknown'
          ? 'status stale'
          : m.breaching.length > 0
            ? `${m.breaching.length} breaching`
            : 'threshold crossed',
      stamp: m.updated_at ? relTime(+new Date(m.updated_at), now) : '—',
      ok: false,
      band,
      link: m.link,
    }));

  // Worst signals first (stable within equal bands: components, then metrics).
  const checks: ChipCheck[] = [...healthChecks, ...metricChecks].sort(
    (a, b) => BAND_RANK[b.band] - BAND_RANK[a.band],
  );

  const activeTotal = activeCounts.critical + activeCounts.error + activeCounts.warn;
  const band: Band = tier === 'error' ? 'critical' : tier;
  return {
    checks,
    tier,
    activeCounts,
    activeTotal,
    ackedCount,
    topAlerts,
    alertsStale: inp.alertsEnabled && inp.alertsError,
    band,
    ok: band === 'ok',
    worstSeverity: worst,
  };
}
